/**
 * An EBTS field reference in a workflow's mapping — MIRROR of services/lib/eft/ebtsRef.ts.
 *
 * Duplicated because the two workspaces do not share a package, the same way consent.ts is. The two must
 * stay identical: the builder here and the stored-EFT editor there have to agree on where a mapped
 * demographic lives, and a divergence would put a value in a different field on each path.
 *: `2.037`, `1.007`, `2.067.1.3`.
 *
 * The mapping used to name Type-2 fields only, as a bare number (`37`, `67.1.1`), because every mappable
 * demographic lived in the descriptive record. Type-1 fields are needed too — the destination agency and
 * the originating agency are per-transaction values for some agencies, not workflow constants — so a
 * reference now carries the record it belongs to.
 *
 * `RR.FFF` with the field zero-padded to at least three digits is the specification's own spelling (2.037
 * is how the EBTS names that field), which makes the padding a free discriminator: the legacy form never
 * padded, so `37` and `2.037` cannot be confused, and neither can `67.1.1` (old: field 67, subfield 1,
 * item 1) and `2.067.1.1`. Without that the two forms are ambiguous — `2.037` in the old grammar reads as
 * field 2, subfield 37 — and a mis-parse silently writes a real value into the wrong field.
 *
 * Legacy references are still accepted, and mean Type-2 as they always did. Nothing needs to be rewritten
 * for the app to keep working; the stored configurations are migrated so the console shows one convention.
 */

export class EbtsRefError extends Error {}

export interface EbtsRef {
  /** Record type: 1 for Type-1, 2 for Type-2. */
  record: number;
  field: number;
  subfield: number;
  item: number;
  /**
   * The reference as the record's own field map keys it — `field[.subfield[.item]]`, with trailing 1s
   * dropped. This is what the builder and AniJs coordinates use, and it never carries the record type,
   * because by then which record is already decided.
   */
  key: string;
}

/** Canonical spelling: `RR.FFF`, plus subfield/item when either is not 1. */
export function formatEbtsRef(ref: Pick<EbtsRef, 'record' | 'field'> & Partial<EbtsRef>): string {
  const sub = ref.subfield ?? 1;
  const item = ref.item ?? 1;
  const base = `${ref.record}.${String(ref.field).padStart(3, '0')}`;
  if (item !== 1) return `${base}.${sub}.${item}`;
  if (sub !== 1) return `${base}.${sub}`;
  return base;
}

/**
 * The key AniJson uses for this coordinate.
 *
 * Its rule, verbatim: a field is keyed by its BARE NUMBER when it holds exactly one subfield containing
 * exactly one item, and by `field.subfield.item` otherwise. There is no two-part form.
 *
 * This used to emit `73.2` for subfield 2 item 1, which AniJson cannot read as a position — so SWFT's
 * SON, SOI and IPAC (2.073 subfields 1, 2 and 3) all collapsed onto field 73 and the last one written
 * was the only one that survived. Anything with a subfield or an item therefore spells out all three
 * parts, even when the item is 1.
 */
function keyOf(field: number, subfield: number, item: number): string {
  if (subfield !== 1 || item !== 1) return `${field}.${subfield}.${item}`;
  return String(field);
}

/**
 * Parse a mapping value into a record + field coordinate.
 *
 * A reference whose SECOND segment is three or more digits is the qualified form; anything else is the
 * legacy Type-2 form. Throws rather than guessing — a mapping that cannot be read must stop the build, not
 * quietly write nothing, because a demographic silently missing from a submission is what the FBI rejects.
 */
export function parseEbtsRef(value: string): EbtsRef {
  const raw = (value ?? '').trim();
  if (!raw) throw new EbtsRefError('Empty EBTS field reference');
  const parts = raw.split('.');
  if (parts.length > 4 || parts.some((p) => !/^\d+$/.test(p))) {
    throw new EbtsRefError(`"${value}" is not an EBTS field reference (expected e.g. 2.037 or 1.007)`);
  }

  /*
   * Three OR MORE digits. EBTS defines user-defined Type-2 fields above 999 (`2.2007`), so an exact
   * three-digit test read those as the legacy form — field 2, subfield 2007 — and would have written a
   * demographic into field 2. A legacy value's second segment is a subfield or item index, never three
   * digits wide, so the boundary stays unambiguous.
   */
  const qualified = parts.length >= 2 && parts[1]!.length >= 3;
  const nums = parts.map((p) => Number(p));
  const record = qualified ? nums[0]! : 2;
  const field = qualified ? nums[1]! : nums[0]!;
  const subfield = (qualified ? nums[2] : nums[1]) ?? 1;
  const item = (qualified ? nums[3] : nums[2]) ?? 1;

  if (record < 1 || record > 99) throw new EbtsRefError(`Record type ${record} is not valid in "${value}"`);
  // Field 1 is the record length, recomputed on write — mapping onto it can only corrupt the record.
  if (field < 2) throw new EbtsRefError(`Field ${field} cannot be mapped (field 1 is the record length)`);
  if (!subfield || !item) throw new EbtsRefError(`Subfield and item are 1-based in "${value}"`);

  return { record, field, subfield, item, key: keyOf(field, subfield, item) };
}

/** Parse, or undefined for anything unusable — for display paths that must not throw. */
export function tryParseEbtsRef(value: string): EbtsRef | undefined {
  try {
    return parseEbtsRef(value);
  } catch {
    return undefined;
  }
}

/**
 * Rewrite a legacy reference in canonical form, leaving an already-qualified one alone.
 *
 * Used when a workflow is saved, so the console converges on one convention without an admin having to
 * retype anything, and used by the migration that upgraded the shipped defaults.
 */
export function canonicalEbtsRef(value: string): string {
  return formatEbtsRef(parseEbtsRef(value));
}
